// Helpers for workflow "Collect customer reply" (chatinput) turns — #144422.
//
// The widget decides how to shape a composer send from its LOCAL view of the
// awaiting workflow step (`currentBotAction`). That view can be stale: the
// step's BOT comment is a hidden-action comment delivered over WS, and it can
// be missed (widget still loading the new conversation, event gated while
// closed, fetch ordering). When that happens the composer sends the typed
// answer as `question` and the server — which resolves the step from the
// ticket — reads `text` for chatinput and stores "". Two guards:
//
//   1. withCollectedAnswerText: every text send also carries `text`, so the
//      server's chatinput branch always finds the answer.
//   2. shouldRefetchForMissingAction: while a bot conversation shows NO
//      awaiting step at all, the widget re-fetches the thread a few times so a
//      missed buttons/chatinput comment still renders (the customer was left
//      on "Kai is typing" with no buttons).

type AnswerPayload = Record<string, any>;

export const withCollectedAnswerText = (data: AnswerPayload): AnswerPayload => {
  if (!data || typeof data !== "object") {
    return data;
  }
  if (typeof data.text === "string" && data.text.trim().length > 0) {
    return data;
  }
  if (Array.isArray(data.text)) {
    return data;
  }
  const fallback = [data.message, data.question].find(
    (v) => typeof v === "string" && v.trim().length > 0,
  );
  return fallback === undefined ? data : { ...data, text: fallback };
};

export const shouldRefetchForMissingAction = (args: {
  botActive: boolean | undefined;
  conversationClosed: boolean | undefined;
  hasAction: boolean;
  hasAnswerBotReply: boolean;
}): boolean => {
  if (!args.botActive || args.conversationClosed) {
    return false;
  }
  if (args.hasAction) {
    return false;
  }
  // A conversation that already has an AI answer is in a free-text
  // answerbotflow turn: no awaiting step is expected there.
  if (args.hasAnswerBotReply) {
    return false;
  }
  return true;
};

// Back-off schedule (ms) for the missing-action refetch. Short first hop: the
// first workflow comment lands ~1s after the conversation is created.
export const MISSING_ACTION_REFETCH_DELAYS_MS = [1500, 3000, 6000];
